import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Upload, FileText } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export const CopedantImportPage = () => {
  const navigate = useNavigate();
  const [csvContent, setCsvContent] = useState('');
  const [importName, setImportName] = useState('');
  const [importing, setImporting] = useState(false);

  const handleImport = async () => {
    if (!csvContent.trim()) {
      toast.error('Please enter CSV content');
      return;
    }

    setImporting(true);
    try {
      await api.post(
        '/copedants/import-csv',
        { csv_content: csvContent, name: importName || 'Imported Copedant' }
      );
      toast.success('Copedant imported successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error importing CSV:', error);
      const message = error.response?.data?.detail || 'Failed to import CSV';
      // Check if upgrade required
      if (message.startsWith('UPGRADE_REQUIRED:')) {
        toast.error(message.replace('UPGRADE_REQUIRED:', ''));
        navigate('/upgrade');
        return;
      }
      toast.error(message);
    } finally {
      setImporting(false);
    }
  };

  const sampleCSV = `string_number,open_note,A,B,LKL
1,F#,0,0,0
2,D#,0,0,0
3,G#,0,1,-1
4,E,0,0,0
5,B,2,0,0
6,G#,0,1,-1
7,F#,0,0,0
8,E,0,0,0
9,D,0,0,0
10,B,2,0,0`;

  return (
    <div className="min-h-screen bg-ss-bg">
      <div className="noise-overlay"></div>

      {/* Header */}
      <header className="sticky top-0 z-50 glass-panel">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            data-testid="back-btn"
            className="text-ss-muted hover:text-ss-text"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="ml-4">
            <h1 className="font-heading text-xl font-bold tracking-tight uppercase text-ss-text">
              Import Copedant
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        <Card className="bg-ss-surface border-ss-border">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Upload className="w-5 h-5 text-ss-cyan" />
              Import from CSV
            </CardTitle>
            <CardDescription className="text-ss-muted">
              Paste your CSV content below. The first row should contain headers.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label className="text-ss-muted">Name</Label>
              <Input
                value={importName}
                onChange={(e) => setImportName(e.target.value)}
                placeholder="My Custom Copedant"
                className="mt-1 bg-ss-bg border-ss-border text-ss-text"
                data-testid="import-name-input"
              />
            </div>

            <div>
              <Label className="text-ss-muted">CSV Content</Label>
              <textarea
                value={csvContent}
                onChange={(e) => setCsvContent(e.target.value)}
                placeholder="string_number,open_note,A,B,C..."
                className="w-full h-64 mt-1 px-3 py-2 bg-ss-bg border border-ss-border rounded-md text-ss-text font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ss-cyan"
                data-testid="csv-content-input"
              />
            </div>

            {/* Sample CSV Preview */}
            <div className="p-4 bg-ss-bg border border-ss-border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-4 h-4 text-ss-muted" />
                <span className="text-sm font-semibold text-ss-muted">Example CSV Format</span>
              </div>
              <pre className="text-xs text-ss-muted font-mono overflow-x-auto">
                {sampleCSV}
              </pre>
              <Button
                variant="link"
                className="text-ss-cyan p-0 h-auto mt-2"
                onClick={() => setCsvContent(sampleCSV)}
              >
                Use this sample
              </Button>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="flex-1 border-ss-border text-ss-text"
              >
                Cancel
              </Button>
              <Button
                onClick={handleImport}
                disabled={importing}
                data-testid="import-btn"
                className="flex-1 bg-ss-cyan hover:bg-ss-cyan/90 text-black font-semibold"
              >
                {importing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Import
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default CopedantImportPage;
